import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, Play, Square, Trash2, Sparkles, Loader2 } from 'lucide-react';
import { onsiteApiFetch } from '../config/api.js';
import { formatMinutes } from '../lib/utils.js';
import type { OnsiteTimeLog, TimeLogCategory } from '../../../api/src/types/onsite-types.js';

type Category = TimeLogCategory;

const CATEGORIES: { value: Category; label: string }[] = [
  { value: 'travel',       label: 'Travel'        },
  { value: 'on_site',      label: 'On Site'       },
  { value: 'remote_assist', label: 'Remote Assist' },
];

function formatElapsed(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return [h > 0 ? `${h}h` : null, `${m.toString().padStart(2, '0')}m`, `${s.toString().padStart(2, '0')}s`]
    .filter(Boolean).join(' ');
}

export default function TimeLogPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [timerStart, setTimerStart] = useState<Date | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [category, setCategory] = useState<Category>('on_site');
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['timelogs', id],
    queryFn: () => onsiteApiFetch<{ timelogs: OnsiteTimeLog[] }>(
      `/api/v1/onsite/timelog?job_id=${id}`
    ),
    enabled: !!id,
  });

  const timelogs = data?.timelogs ?? [];

  // Detect active timer from server data (timelog with ended_at = null)
  const serverActiveEntry = useMemo(
    () => timelogs.find(t => !t.ended_at),
    [timelogs]
  );

  // Sync server state with local timer
  useEffect(() => {
    if (serverActiveEntry && !timerStart) {
      setTimerStart(new Date(serverActiveEntry.started_at));
      setActiveEntryId(serverActiveEntry.id);
      setCategory(serverActiveEntry.category);
    }
  }, [serverActiveEntry, timerStart]);

  const startTimer = useMutation({
    mutationFn: () => onsiteApiFetch<{ timelog: OnsiteTimeLog }>('/api/v1/onsite/timelog', {
      method: 'POST',
      body: JSON.stringify({
        job_id: id,
        category,
        started_at: new Date().toISOString(),
        billable: true,
      }),
    }),
    onSuccess: d => {
      setActiveEntryId(d.timelog.id);
      setTimerStart(new Date());
      qc.invalidateQueries({ queryKey: ['timelogs', id] });
    },
  });

  const stopTimer = useMutation({
    mutationFn: (entryId: string) =>
      onsiteApiFetch(`/api/v1/onsite/timelog/${entryId}`, {
        method: 'PATCH',
        body: JSON.stringify({ ended_at: new Date().toISOString() }),
      }),
    onSuccess: () => {
      setTimerStart(null);
      setActiveEntryId(null);
      setElapsed(0);
      qc.invalidateQueries({ queryKey: ['timelogs', id] });
    },
  });

  const deleteEntry = useMutation({
    mutationFn: (entryId: string) =>
      onsiteApiFetch(`/api/v1/onsite/timelog/${entryId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['timelogs', id] }),
  });

  const categorize = useMutation({
    mutationFn: async ({ entryId }: { entryId: string }) => {
      const r = await onsiteApiFetch<{ category: Category }>(
        `/api/v1/onsite/timelog/${entryId}/category-suggest`,
        { method: 'POST' }
      );
      await onsiteApiFetch(`/api/v1/onsite/timelog/${entryId}`, {
        method: 'PATCH',
        body: JSON.stringify({ category: r.category }),
      });
      return r;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['timelogs', id] }),
  });

  // Timer tick
  useEffect(() => {
    if (!timerStart) return;
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - timerStart.getTime()) / 1000));
      setTick(t => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timerStart]);

  const totalBillable = timelogs
    .filter(t => t.billable && t.duration_minutes)
    .reduce((sum, t) => sum + (t.duration_minutes ?? 0), 0);

  // keep `tick` used
  void tick;

  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Time Log</h1>
          {totalBillable > 0 && (
            <p className="text-sm text-green-400">{formatMinutes(totalBillable)} billable</p>
          )}
        </div>
        <button
          onClick={() => navigate(`/jobs/${id}`)}
          className="rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-secondary"
        >
          Back
        </button>
      </div>

      {/* Active timer card (prominent) */}
      {timerStart && activeEntryId && (
        <div className="mb-4 rounded-xl border border-primary/40 bg-primary/10 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">Active Timer</p>
              <p className="text-xs text-muted-foreground capitalize">{category.replace('_', ' ')}</p>
            </div>
            <div className="text-3xl font-mono font-bold text-primary">
              {formatElapsed(elapsed)}
            </div>
          </div>
          <button
            onClick={() => stopTimer.mutate(activeEntryId)}
            disabled={stopTimer.isPending}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-6 py-2.5 font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            <Square className="h-4 w-4 fill-current" />
            Stop Timer
          </button>
        </div>
      )}

      {/* Timer controls (start) */}
      {!timerStart && (
        <div className="mb-4 rounded-xl border border-border bg-card p-4">
          <div className="space-y-3">
            <div>
              <label className="mb-1.5 block text-xs text-muted-foreground">Category</label>
              <div className="grid grid-cols-3 gap-2">
                {CATEGORIES.map(c => (
                  <button
                    key={c.value}
                    onClick={() => setCategory(c.value)}
                    className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      category === c.value
                        ? 'bg-primary text-foreground'
                        : 'border border-border bg-secondary text-muted-foreground hover:bg-secondary'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => startTimer.mutate()}
              disabled={startTimer.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-semibold text-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <Play className="h-5 w-5 fill-current" />
              Start Timer
            </button>
          </div>
        </div>
      )}

      {/* Log entries */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : timelogs.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground">
            <Clock className="h-10 w-10" />
            <p className="font-medium">No time logged yet</p>
            <p className="text-sm text-muted-foreground text-center max-w-xs">Start the timer above to track your time on this job — entries will appear here for billing.</p>
          </div>
        ) : (
          timelogs.map(entry => {
            const needsCategory = !entry.category || (entry.category as string) === '';
            const isCategorizing = categorize.isPending && categorize.variables?.entryId === entry.id;
            return (
              <div key={entry.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
                <Clock className="h-5 w-5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-foreground capitalize">
                      {entry.category ? entry.category.replace('_', ' ') : 'Uncategorized'}
                    </p>
                    {entry.billable && (
                      <span className="rounded-full bg-green-500/20 px-1.5 py-0.5 text-xs text-green-400">
                        billable
                      </span>
                    )}
                    {!entry.ended_at && (
                      <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-xs text-primary">
                        active
                      </span>
                    )}
                    {needsCategory && (
                      <button
                        onClick={() => categorize.mutate({ entryId: entry.id })}
                        disabled={isCategorizing}
                        className="flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary hover:bg-primary/20 disabled:opacity-50"
                      >
                        {isCategorizing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                        AI Categorize
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {entry.duration_minutes != null
                      ? formatMinutes(entry.duration_minutes)
                      : 'in progress'}
                    {entry.notes && ` · ${entry.notes}`}
                  </p>
                </div>
                <button
                  onClick={() => deleteEntry.mutate(entry.id)}
                  className="rounded p-1.5 text-muted-foreground hover:bg-red-500/10 hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
